/**
 * LEK (Local Ecological Knowledge) Habitat data layer - species + season controls.
 * Uses useDataLayer for MapController and setLayerDefaultValues; fetches species via API.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { getRegistryData } from '../../../pages/registry/componentRegistry';
import { useDataLayer } from '../hooks/useDataLayer';
import { useApi } from '../../../api/api';
import { useFormContextOptional } from '../../../contexts/FormContext';
import Legend from '../Legend/Legend';

const FORM_KEY_VISIBLE = 'show-lek-species-info';
const FORM_KEY_SPECIES = 'lek-selected-species';

const SEASONS = ['Winter', 'Spring', 'Summer', 'Fall', 'None'] as const;
type Season = (typeof SEASONS)[number];

interface LekDefaultValues {
  default?: {
    season?: string;
    Season?: string;
    species?: string;
  };
  season?: string;
  species_uuid?: string;
  species_id?: string | number;
}

interface LekSpeciesItem {
  species_id: number;
  species_uuid?: string | null;
  common_name?: string;
  species_name?: string;
}

interface MonthRange {
  start: number;
  end: number;
}

/** LEK API slice from useApi(); useApi() is untyped in api.js so we assert this in the component. */
interface LekApiSlice {
  getLekSpecies: () => Promise<{ data?: LekSpeciesItem[] }>;
  getLekHabitatData: (
    speciesIdentifier: string,
    monthRange: { start: number; end: number }
  ) => Promise<{ data?: unknown }>;
}

/** Normalize season from default values (e.g. "SPRING") to display value ("Spring"). */
function normalizeSeason(value: unknown): Season {
  if (value == null || value === '') return 'None';
  const s = String(value).toLowerCase();
  const match = SEASONS.find((season) => season.toLowerCase() === s);
  return (match ?? 'None') as Season;
}

/** Read species identifier from default values: prefer data.default.species, then data.species_uuid / data.species_id. */
function getInitialSpecies(data: LekDefaultValues | undefined): string {
  const v = data?.default?.species ?? data?.species_uuid ?? data?.species_id ?? '';
  return v !== undefined && v !== null ? String(v) : '';
}

function getMonthRangeForSeason(season: string): MonthRange | null {
  switch (String(season)) {
    case 'Winter':
      return { start: 12, end: 2 };
    case 'Spring':
      return { start: 3, end: 5 };
    case 'Summer':
      return { start: 6, end: 8 };
    case 'Fall':
      return { start: 9, end: 11 };
    default:
      return null;
  }
}

const mountIdRef = { next: 0 };

export default function LekHabitatDataLayer(props: RegistryComponentProps): React.ReactElement {
  const { slug, isReady, isActive, setActive, setLayerDefaultValues, data, mapController } =
    useDataLayer(props);
  const formContext = useFormContextOptional();
  const { onSpeciesLEKLoad, isHidden } = getRegistryData<{
    onSpeciesLEKLoad?: (speciesId: string | number | null) => void;
    isHidden?: boolean;
  }>(props);
  const { getLekSpecies, getLekHabitatData } = useApi() as unknown as LekApiSlice;
  const getLekSpeciesRef = useRef(getLekSpecies);
  const getLekHabitatDataRef = useRef(getLekHabitatData);
  useEffect(() => { getLekSpeciesRef.current = getLekSpecies; });
  useEffect(() => { getLekHabitatDataRef.current = getLekHabitatData; });
  const [species, setSpecies] = useState<LekSpeciesItem[]>([]);
  const [speciesLoading, setSpeciesLoading] = useState(true);
  const [lekError, setLekError] = useState<string | null>(null);
  // Read from layout default values, then fall back to map controller filters (set by initializeLayers or other instance)
  const lekData = data as LekDefaultValues | undefined;
  const [season, setSeason] = useState<Season>(() => {
    const fromData = normalizeSeason(lekData?.default?.season ?? lekData?.season);
    if (fromData !== 'None') return fromData;
    const fromFilter = (mapController as { getFilter?: (k: string) => unknown })?.getFilter?.('lekSeason');
    return normalizeSeason(fromFilter);
  });
  const [selectedSpecies, setSelectedSpecies] = useState(() => {
    const fromData = getInitialSpecies(lekData);
    if (fromData) return fromData;
    const fromFilter = (mapController as { getFilter?: (k: string) => unknown })?.getFilter?.('lekSpecies');
    return fromFilter != null ? String(fromFilter) : '';
  });
  const defaultsAppliedRef = useRef(false);
  const fetchInFlightRef = useRef(false);
  const instanceIdRef = useRef<string | null>(null);
  if (instanceIdRef.current === null) {
    mountIdRef.next += 1;
    instanceIdRef.current = `lek-${slug}-${mountIdRef.next}`;
  }

  // When we have no defaults from props but the map controller already has LEK filters (e.g. from initializeLayers), sync state once so the visible instance shows the right values
  const syncedFromControllerRef = useRef(false);
  useEffect(() => {
    if (syncedFromControllerRef.current) return;
    const mc = mapController as { getFilter?: (k: string) => unknown } | null;
    if (!mc?.getFilter) return;
    if (season !== 'None' && selectedSpecies) return;
    const filterSeason = mc.getFilter('lekSeason');
    const filterSpecies = mc.getFilter('lekSpecies');
    const nextSeason = normalizeSeason(filterSeason);
    const nextSpecies = filterSpecies != null && filterSpecies !== '' ? String(filterSpecies) : '';
    if (nextSeason !== 'None' || nextSpecies) {
      if (nextSeason !== 'None') setSeason(nextSeason);
      if (nextSpecies) setSelectedSpecies(nextSpecies);
      syncedFromControllerRef.current = true;
    }
  }, [mapController, season, selectedSpecies]);

  // Apply layout default values when they become available (run once per mount so we don't overwrite user changes).
  // useLayoutEffect so we run before paint and aren't overwritten by other effects.
  useLayoutEffect(() => {
    const skip = defaultsAppliedRef.current || !lekData?.default;
    if (skip) return;
    const def = lekData?.default;
    if (!def) return;
    const nextSeason = normalizeSeason(def.season ?? def.Season);
    const nextSpecies =
      def.species != null && def.species !== '' ? String(def.species) : null;
    if (nextSeason !== 'None') {
      setSeason(nextSeason);
      setLayerDefaultValues('season', nextSeason);
    }
    if (nextSpecies) {
      setSelectedSpecies(nextSpecies);
      setLayerDefaultValues('species_uuid', nextSpecies);
    }
    defaultsAppliedRef.current = true;
  }, [lekData?.default, setLayerDefaultValues]);

  // Once species list has loaded, ensure selectedSpecies is set to layout default if we have one (so dropdown shows it)
  const defaultSpeciesAppliedRef = useRef(false);
  useEffect(() => {
    if (speciesLoading || species.length === 0 || !lekData?.default?.species || defaultSpeciesAppliedRef.current) return;
    const defaultSpecies = String(lekData.default.species);
    const inList = species.some(
      (s) => String(s.species_uuid ?? s.species_id).toLowerCase() === defaultSpecies.toLowerCase()
    );
    if (inList) {
      setSelectedSpecies(defaultSpecies);
      defaultSpeciesAppliedRef.current = true;
    }
  }, [speciesLoading, species, lekData?.default?.species]);

  // Fetch species list once on mount
  useEffect(() => {
    let cancelled = false;
    getLekSpeciesRef.current()
      .then((res: { data?: LekSpeciesItem[] }) => {
        if (!cancelled && res?.data)
          setSpecies(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error('Species fetch failed:', err);
      })
      .finally(() => {
        if (!cancelled) setSpeciesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-activate layer when species is selected and season is not None
  useEffect(() => {
    if (!isReady || !setActive) return;
    const shouldBeActive = Boolean(selectedSpecies) && season !== 'None';
    if (shouldBeActive && !isActive) {
      setActive(true);
    } else if (!shouldBeActive && isActive) {
      setActive(false);
    }
  }, [isReady, selectedSpecies, season, isActive, setActive]);

  // Fetch habitat data when layer is active with valid species + season and push to map
  useEffect(() => {
    if (!mapController?.setLekHabitatData) return;
    if (!isActive || !selectedSpecies || season === 'None') return;
    if (fetchInFlightRef.current) return;
    const monthRange = getMonthRangeForSeason(season);
    if (!monthRange) return;

    fetchInFlightRef.current = true;
    let cancelled = false;
    getLekHabitatDataRef.current(selectedSpecies, { start: monthRange.start, end: monthRange.end })
      .then((res: { data?: unknown }) => {
        if (!cancelled && res?.data) {
          setLekError(null);
          mapController.setLekHabitatData(res.data);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const status = (err as { response?: { status?: number } }).response?.status;
          const message = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
          if (status === 403 && message) {
            setLekError(message);
          } else {
            console.error('LEK habitat data fetch failed:', err);
          }
          mapController.setLekHabitatData([]);
        }
      })
      .finally(() => {
        fetchInFlightRef.current = false;
      });
    return () => {
      cancelled = true;
      fetchInFlightRef.current = false;
    };
  }, [isActive, selectedSpecies, season, mapController]);

  const handleSeasonChange = (s: Season): void => {
    setSeason(s);
    setLayerDefaultValues('season', s);
  };

  const handleSpeciesChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = e.target.value;
    setSelectedSpecies(value);
    setLayerDefaultValues('species_uuid', value || null);
    setLekError(null);
  };

  // Sync FormContext and trigger species LEK fetch when selectedSpecies changes. Skip hidden instance
  // (preload) so it doesn't overwrite with empty and hide lek-species-info. Only depend on selectedSpecies
  // to avoid loop: formContext changes when setValue runs, which would re-trigger this effect.
  const lastSyncedSpeciesRef = useRef<string | null>(null);
  useEffect(() => {
    if (isHidden) return;
    const value = selectedSpecies || null;
    const valueKey = value === null ? '__none__' : String(value);
    if (lastSyncedSpeciesRef.current === valueKey) return;
    lastSyncedSpeciesRef.current = valueKey;
    formContext?.setValue?.(FORM_KEY_VISIBLE, Boolean(value));
    formContext?.setValue?.(FORM_KEY_SPECIES, value);
    onSpeciesLEKLoad?.(value);
  }, [selectedSpecies, isHidden]);

  return (
    <div className="control-section" style={{ gap: '0px' }}>
      <div className="section-title" style={{ paddingBottom: '0px' }}>
        <span className="section-icon" style={{ paddingBottom: '0px' }}>
          Local Ecological Knowledge
        </span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '18px',
          fontStyle: 'italic',
        }}
      >
        Shows where fishermen expect to find fish by season
        <Legend />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor={`lek-species-select-${slug}`}>
          Fish Species
        </label>
        <select
          id={`lek-species-select-${slug}`}
          className="form-control"
          value={selectedSpecies}
          onChange={handleSpeciesChange}
          disabled={speciesLoading || !isReady}
        >
          <option value="">{speciesLoading ? 'Loading species...' : 'None'}</option>
          {/* If layout default species isn't in the API list yet, show it so the select isn't blank */}
          {selectedSpecies &&
            !species.some(
              (s) => String(s.species_uuid ?? s.species_id).toLowerCase() === selectedSpecies.toLowerCase()
            ) && (
              <option value={selectedSpecies}>
                {speciesLoading ? 'Loading...' : 'Selected species'}
              </option>
            )}
          {species.map((s) => {
            const idOrUuid = s.species_uuid ?? s.species_id;
            return (
              <option key={String(idOrUuid)} value={String(idOrUuid)}>
                {s.common_name || s.species_name}
              </option>
            );
          })}
        </select>
        {lekError && (
          <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#dc3545' }}>{lekError}</p>
        )}
      </div>
      <div
        className="form-group lek-season-radiogroup"
        style={{ marginBottom: '0px' }}
        role="radiogroup"
        aria-label="Season"
      >
        {SEASONS.map((s) => {
          const id = `lek-season-${instanceIdRef.current}-${s.toLowerCase()}`;
          const name = `lek-season-${instanceIdRef.current}`;
          return (
            <label
              key={s}
              htmlFor={id}
              className="lek-season-label"
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={s}
                checked={season === s}
                onChange={() => handleSeasonChange(s)}
                disabled={speciesLoading || !isReady}
                style={{ marginRight: '8px', appearance: 'radio' }}
              />
              <span>{s}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
